import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Receipt, AlertTriangle, Save, RefreshCw } from "lucide-react";

function emptyTipo() {
  return { codigo_sri: "", descripcion: "", tipo: "renta", porcentaje: 0, activo: true };
}

export default function AdminTiposRetencion() {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | renta | iva
  const [dialog, setDialog] = useState(null); // { mode: "create"|"edit", data }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tipos-retencion");
      setTipos(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const open = (mode, data = null) => setDialog({ mode, data: data ? { ...data } : emptyTipo() });
  const close = () => setDialog(null);

  const save = async () => {
    const d = dialog.data;
    if (!d.codigo_sri || !d.codigo_sri.trim()) return toast.error("Código SRI obligatorio");
    if (!d.descripcion || !d.descripcion.trim()) return toast.error("Descripción obligatoria");
    if (d.porcentaje == null || isNaN(parseFloat(d.porcentaje))) return toast.error("Porcentaje inválido");
    try {
      setSaving(true);
      const payload = {
        codigo_sri: d.codigo_sri.trim(),
        descripcion: d.descripcion.trim(),
        tipo: d.tipo,
        porcentaje: parseFloat(d.porcentaje),
        activo: d.activo !== false,
      };
      if (dialog.mode === "create") {
        await api.post("/tipos-retencion", payload);
        toast.success(`Código ${payload.codigo_sri} creado`);
      } else {
        await api.patch(`/tipos-retencion/${d.id}`, payload);
        toast.success(`Código ${payload.codigo_sri} actualizado`);
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Desactivar el código ${t.codigo_sri}? Ya no aparecerá en el modal de retenciones.`)) return;
    try {
      await api.delete(`/tipos-retencion/${t.id}`);
      toast.success("Código desactivado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = tipos.filter(t => filter === "all" ? true : t.tipo === filter);
  const renta = tipos.filter(t => t.tipo === "renta").length;
  const iva = tipos.filter(t => t.tipo === "iva").length;

  return (
    <div className="space-y-6" data-testid="admin-tipos-retencion-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Administración</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Receipt className="inline mr-2 -mt-1" size={28} />
          Catálogo SRI · Tipos de retención
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">
          Actualice aquí los porcentajes vigentes según el SRI Ecuador. Los cambios se aplican a las nuevas retenciones que genere.
        </p>
      </div>

      <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-sm flex items-start gap-2">
        <AlertTriangle size={16} className="text-[#8a5500] mt-0.5 flex-shrink-0" />
        <div className="text-[#8a5500]">
          <strong>Importante:</strong> los porcentajes que registre aquí se usan al sugerir y calcular las retenciones SRI de futuras facturas de compra.
          Las retenciones ya emitidas no cambian. Si el SRI publicó una nueva tabla (por ejemplo a partir del 1-marzo-2026), edite los códigos correspondientes con sus nuevos valores.
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")} className={filter === "all" ? "bg-[#205c45]" : ""} data-testid="filter-all">
            Todos <Badge variant="secondary" className="ml-2">{tipos.length}</Badge>
          </Button>
          <Button variant={filter === "renta" ? "default" : "outline"} size="sm" onClick={() => setFilter("renta")} className={filter === "renta" ? "bg-[#205c45]" : ""} data-testid="filter-renta">
            IR (Renta) <Badge variant="secondary" className="ml-2">{renta}</Badge>
          </Button>
          <Button variant={filter === "iva" ? "default" : "outline"} size="sm" onClick={() => setFilter("iva")} className={filter === "iva" ? "bg-[#205c45]" : ""} data-testid="filter-iva">
            IVA <Badge variant="secondary" className="ml-2">{iva}</Badge>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            if (!window.confirm("¿Aplicar los porcentajes oficiales SRI 2025 a TODOS los códigos del catálogo?\n\nSe actualizarán los códigos existentes con su porcentaje vigente y se crearán los que falten. No afecta retenciones ya emitidas.")) return;
            try {
              const { data } = await api.post("/tipos-retencion/reseed");
              toast.success(`Catálogo actualizado · ${data.actualizados} actualizados, ${data.creados} nuevos`);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }} data-testid="reseed-tipos-btn">
            <RefreshCw size={14} className="mr-2" /> Aplicar tabla SRI 2025
          </Button>
          <Button onClick={() => open("create")} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-tipo-btn">
            <Plus size={16} className="mr-2" /> Nuevo código SRI
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead>
            <tr>
              <th className="w-24">Tipo</th>
              <th className="w-32">Código SRI</th>
              <th>Descripción</th>
              <th className="w-24 text-right">% Retención</th>
              <th className="w-24 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center text-[#8ba397]">Cargando…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-[#8ba397]">Sin códigos en este filtro</td></tr>
            ) : (
              filtered.map(t => (
                <tr key={t.id} data-testid={`tipo-row-${t.codigo_sri}`}>
                  <td>
                    <Badge variant="outline" className={`capitalize ${t.tipo === "renta" ? "border-[#205c45] text-[#205c45]" : "border-[#8a5500] text-[#8a5500]"}`}>
                      {t.tipo === "renta" ? "IR (Renta)" : "IVA"}
                    </Badge>
                  </td>
                  <td className="font-mono text-sm font-semibold">{t.codigo_sri}</td>
                  <td className="text-sm">{t.descripcion}</td>
                  <td className="text-right font-bold tabular-nums text-base">{(+t.porcentaje).toFixed(2)}%</td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => open("edit", t)} data-testid={`edit-${t.codigo_sri}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(t)} data-testid={`del-${t.codigo_sri}`}>
                        <Trash2 size={14} className="text-[#b53a25]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-xl" data-testid="tipo-form-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "create" ? "Nuevo código SRI" : `Editar ${dialog?.data?.codigo_sri}`}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={dialog.data.tipo} onValueChange={(v) => setDialog(d => ({ ...d, data: { ...d.data, tipo: v } }))}>
                    <SelectTrigger data-testid="tipo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="renta">IR (Renta)</SelectItem>
                      <SelectItem value="iva">IVA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Código SRI</Label>
                  <Input
                    value={dialog.data.codigo_sri}
                    onChange={(e) => setDialog(d => ({ ...d, data: { ...d.data, codigo_sri: e.target.value } }))}
                    placeholder="312"
                    data-testid="codigo-input"
                  />
                </div>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={dialog.data.descripcion}
                  onChange={(e) => setDialog(d => ({ ...d, data: { ...d.data, descripcion: e.target.value } }))}
                  placeholder="Transferencia de bienes muebles…"
                  data-testid="desc-input"
                />
              </div>
              <div>
                <Label>Porcentaje (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={dialog.data.porcentaje}
                  onChange={(e) => setDialog(d => ({ ...d, data: { ...d.data, porcentaje: e.target.value } }))}
                  placeholder="1.75"
                  data-testid="pct-input"
                  className="text-lg h-11 tabular-nums"
                />
                <p className="text-[11px] text-[#5c7368] mt-1 italic">
                  Ej: para una retención del 2%, escriba <strong>2</strong>. Para 1.75%, escriba <strong>1.75</strong>.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#205c45]" data-testid="save-tipo-btn">
              <Save size={14} className="mr-2" />
              {saving ? "Guardando…" : (dialog?.mode === "create" ? "Crear" : "Guardar cambios")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
